import type { FtpConnectionType, FtpFileItem, FtpSortConfig } from './types'

export const CONNECTION_LABELS: Record<FtpConnectionType, string> = {
  ukm5: 'УКМ-5',
  ukm4: 'УКМ-4',
  mis: 'МИ',
  scale: 'Весы',
}

const TEXT_EXTENSIONS = new Set([
  'txt', 'js', 'css', 'html', 'json', 'conf', 'bash', 'sh', 'md', 'xml', 'yaml', 'yml',
  'log', 'ini', 'cfg', 'properties', 'sql', 'ts', 'tsx', 'jsx', 'vue', 'env', 'service',
])
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'])
const PDF_EXTENSIONS = new Set(['pdf'])

export function isDirectory(item: FtpFileItem) {
  return item.type === 'directory'
}

export function normalizePath(path: string) {
  let p = path || '/'
  if (!p.startsWith('/')) p = `/${p}`
  return p.replace(/\/+/g, '/').replace(/\/$/, '') || '/'
}

export function parentPath(path: string) {
  if (path === '/') return '/'
  const parts = path.split('/').filter(Boolean)
  parts.pop()
  return parts.length ? `/${parts.join('/')}` : '/'
}

export function joinPath(base: string, name: string) {
  if (base === '/') return `/${name}`
  return `${base}/${name}`
}

export function formatFileSize(bytes: number) {
  if (!bytes || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} Б`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} ГБ`
}

export function formatDate(iso: string | null) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/** API must return a string; guard against object permissions from older backends. */
export function formatPermissionsLabel(permissions: unknown): string {
  if (typeof permissions === 'string' && permissions) return permissions
  if (permissions && typeof permissions === 'object') {
    const p = permissions as { user?: string | number; group?: string | number; world?: string | number; other?: string | number }
    const bit = (v: string | number | undefined) => {
      if (typeof v === 'string') {
        return `${v.includes('r') ? 'r' : '-'}${v.includes('w') ? 'w' : '-'}${v.includes('x') ? 'x' : '-'}`
      }
      const n = Number(v) || 0
      return `${n & 4 ? 'r' : '-'}${n & 2 ? 'w' : '-'}${n & 1 ? 'x' : '-'}`
    }
    const label = bit(p.user) + bit(p.group) + bit(p.world ?? p.other)
    return label === '---------' ? '—' : `-${label}`
  }
  return '—'
}

export function getFileExtension(name: string) {
  const idx = name.lastIndexOf('.')
  if (idx <= 0) return ''
  return name.slice(idx + 1).toLowerCase()
}

export function getOpenAction(name: string): 'text' | 'image' | 'pdf' | 'download' {
  const ext = getFileExtension(name)
  if (TEXT_EXTENSIONS.has(ext)) return 'text'
  if (IMAGE_EXTENSIONS.has(ext)) return 'image'
  if (PDF_EXTENSIONS.has(ext)) return 'pdf'
  return 'download'
}

export function filterAndSortItems(
  items: FtpFileItem[],
  query: string,
  sort: FtpSortConfig,
): FtpFileItem[] {
  let list = [...items]
  if (query.trim()) {
    const q = query.trim().toLowerCase()
    list = list.filter((item) => item.name.toLowerCase().includes(q))
  }

  list.sort((a, b) => {
    if (isDirectory(a) !== isDirectory(b)) {
      return isDirectory(a) ? -1 : 1
    }

    let aVal: string | number
    let bVal: string | number
    switch (sort.field) {
      case 'size':
        aVal = a.size || 0
        bVal = b.size || 0
        break
      case 'date':
        aVal = new Date(a.modifyTime || 0).getTime()
        bVal = new Date(b.modifyTime || 0).getTime()
        break
      default:
        aVal = a.name.toLowerCase()
        bVal = b.name.toLowerCase()
    }

    if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1
    if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1
    return 0
  })

  return list
}

export function totalSize(items: FtpFileItem[]) {
  return items.filter((i) => !isDirectory(i)).reduce((sum, i) => sum + (i.size || 0), 0)
}

export function breadcrumbParts(path: string) {
  const parts = path.split('/').filter(Boolean)
  const segments: { label: string; path: string }[] = [{ label: '/', path: '/' }]
  let current = ''
  for (const part of parts) {
    current += `/${part}`
    segments.push({ label: part, path: current })
  }
  return segments
}
